/**
 * The messages between the app and the DOCX worker (part 22, table E″): the app sends the book as the PDF's table N reads
 * it, each chapter's IDs for its bookmarks (table PN), the preset and the export's moment; the worker says how far it has
 * come and answers with the file, and how many images it couldn't take, or that there is none. Each side reads what it
 * receives field by field (Definition of Done, security), so a message that isn't one of these is never taken for one.
 */
import { imageDestination, MAX_DIFF_LINES, MAX_DIFF_TEXT, readImageDestination } from '@cockatrice/core';
import type { DiffLine, MergedChapter } from '@cockatrice/core';
import type { ChapterIds, DocxPreset } from '@cockatrice/export/docx';
import { fieldsOf, isCount, MAX_MODIFIED } from '../epub/protocol.ts';
import type { EpubReply } from '../epub/protocol.ts';
import { MAX_BOOK_CHAPTERS, MAX_BOOK_TEXT, MAX_CHAPTER_TEXT, readExportBook } from '@cockatrice/layout';
import type { PdfBook } from '@cockatrice/layout';

/** E″1: the DOCX of a book in a preset, made at `modified` (milliseconds since the epoch), its chapters' IDs one each. */
export interface DocxJob {
  readonly kind: 'docx';
  readonly book: PdfBook;
  readonly chapters: readonly ChapterIds[];
  readonly preset: DocxPreset;
  readonly modified: number;
}

/** E″2–E″4: what the worker sends back: the EPUB's replies (part 20's E2–E4), read by its `readReply`. */
export type DocxReply = EpubReply;

/** Part 23b, M″3: one chapter to merge: its base (null: none, CS5), its stored text and the file's reading of it. */
export interface MergeChapter {
  readonly base: string | null;
  readonly current: string;
  readonly file: string;
}

/**
 * Part 23b, M″3: chapters to merge (decision 64, L8, L9), each read against its reference's own export: the book's ID,
 * theme and metadata as its export takes them (its `chapters` empty), and the image destinations the book holds.
 */
export interface MergeJob {
  readonly kind: 'merge';
  readonly book: PdfBook;
  readonly held: readonly string[];
  readonly chapters: readonly MergeChapter[];
}

/** Part 24d, M″5: one chapter of a bundle's update to merge (U5): its base, this device's text and the bundle's. */
export interface SourcesChapter {
  readonly base: string;
  readonly current: string;
  readonly file: string;
}

/** Part 24d, M″5: chapters of a bundle's update merged three ways as Markdown (core's `mergeSources`); the reply is M″4's. */
export interface SourcesJob {
  readonly kind: 'merge-sources';
  readonly chapters: readonly SourcesChapter[];
}

/** What the page asks of one DOCX worker. */
export type DocxWorkerJob = DocxJob | MergeJob | SourcesJob;

/** Part 23b, M″4: chapters merged so far, out of all; then each chapter merged, in the job's order; or none. */
export type MergeReply =
  | { readonly kind: 'progress'; readonly done: number; readonly total: number }
  | { readonly kind: 'merged'; readonly chapters: readonly MergedChapter[] }
  | { readonly kind: 'failed' };

/** M″3: the most image destinations a merge is told the book holds. */
export const MAX_HELD = 100_000;

/** The DOCX file's media type. */
export const DOCX_TYPE = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** H10: an ID as the store makes them. */
const ID = /^[0-9a-f]{32}$/u;

/** E″1: a preset: the theme, or the Manuscript in one of its two faces; null for anything else. */
export function readPreset(value: unknown): DocxPreset | null {
  const theme = fieldsOf(value, ['kind']);
  if (theme?.get('kind') === 'theme') return { kind: 'theme' };
  const manuscript = fieldsOf(value, ['kind', 'font']);
  const font = manuscript?.get('font');
  if (manuscript?.get('kind') !== 'manuscript' || (font !== 'times-new-roman' && font !== 'courier-new')) return null;
  return { kind: 'manuscript', font };
}

/** E″1: a chapter's IDs: its own, and its snapshot's or null; null for anything else. */
function readIds(value: unknown): ChapterIds | null {
  const fields = fieldsOf(value, ['id', 'snapshot']);
  const id = fields?.get('id');
  const snapshot = fields?.get('snapshot');
  if (typeof id !== 'string' || !ID.test(id)) return null;
  if (snapshot === null) return { id, snapshot };
  return typeof snapshot === 'string' && ID.test(snapshot) ? { id, snapshot } : null;
}

/** M″3: whether `value` is an image destination written as the export writes it. */
function isDestination(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const reference = readImageDestination(value);
  return reference !== null && imageDestination(reference) === value;
}

/** M″3: a text of a chapter to merge, or null when it isn't one or passes the chapter's limit. */
function isText(value: unknown): value is string {
  return typeof value === 'string' && value.length <= MAX_CHAPTER_TEXT;
}

/** M″3: a merge job's fields, or null for anything else; the book read as the export reads it, its chapters none. */
function readMerge(value: unknown): MergeJob | null {
  const fields = fieldsOf(value, ['kind', 'book', 'held', 'chapters']);
  const held = fields?.get('held');
  const chapters = fields?.get('chapters');
  if (fields?.get('kind') !== 'merge' || !Array.isArray(held) || held.length > MAX_HELD || !held.every(isDestination)) return null;
  if (!Array.isArray(chapters) || chapters.length > MAX_BOOK_CHAPTERS) return null;
  const read: MergeChapter[] = [];
  let length = 0;
  for (const chapter of chapters) {
    const each = fieldsOf(chapter, ['base', 'current', 'file']);
    const base = each?.get('base');
    const current = each?.get('current');
    const file = each?.get('file');
    if ((base !== null && !isText(base)) || !isText(current) || !isText(file)) return null;
    length += (base === null ? 0 : base.length) + current.length + file.length;
    if (length > 3 * MAX_BOOK_TEXT) return null;
    read.push({ base, current, file });
  }
  let book: PdfBook;
  try {
    book = readExportBook(fields.get('book'));
  } catch {
    return null;
  }
  return book.chapters.length === 0 ? { kind: 'merge', book, held, chapters: read } : null;
}

/** M″5: a sources job's fields, or null for anything else: each chapter's three texts within a chapter's limit, all within the book's three times. */
function readSources(value: unknown): SourcesJob | null {
  const chapters = fieldsOf(value, ['kind', 'chapters'])?.get('chapters');
  if (!Array.isArray(chapters) || chapters.length > MAX_BOOK_CHAPTERS) return null;
  const read: SourcesChapter[] = [];
  let length = 0;
  for (const chapter of chapters) {
    const each = fieldsOf(chapter, ['base', 'current', 'file']);
    const base = each?.get('base');
    const current = each?.get('current');
    const file = each?.get('file');
    if (!isText(base) || !isText(current) || !isText(file)) return null;
    length += base.length + current.length + file.length;
    if (length > 3 * MAX_BOOK_TEXT) return null;
    read.push({ base, current, file });
  }
  return { kind: 'merge-sources', chapters: read };
}

/** M″4: a diff line: removed or added, its text within MAX_DIFF_TEXT; null for anything else. */
function readDiffLine(value: unknown): DiffLine | null {
  const fields = fieldsOf(value, ['change', 'text']);
  const change = fields?.get('change');
  const text = fields?.get('text');
  return (change === 'removed' || change === 'added') && typeof text === 'string' && text.length <= MAX_DIFF_TEXT ? { change, text } : null;
}

/** M″4: one merged chapter, exactly its fields; null for anything else. */
function readMerged(value: unknown): MergedChapter | null {
  const fields = fieldsOf(value, ['text', 'changed', 'fileChanged', 'conflicts', 'diff', 'more']);
  const text = fields?.get('text');
  const changed = fields?.get('changed');
  const fileChanged = fields?.get('fileChanged');
  const conflicts = fields?.get('conflicts');
  const diff = fields?.get('diff');
  const more = fields?.get('more');
  if (!isText(text) || typeof changed !== 'boolean' || typeof fileChanged !== 'boolean' || !isCount(conflicts) || !isCount(more)) return null;
  if (!Array.isArray(diff) || diff.length > MAX_DIFF_LINES) return null;
  const lines: DiffLine[] = [];
  for (const line of diff) {
    const read = readDiffLine(line);
    if (read === null) return null;
    lines.push(read);
  }
  return { text, changed, fileChanged, conflicts, diff: lines, more };
}

/** M″4: what a merging worker sent, or null for anything else. */
export function readMergeReply(value: unknown): MergeReply | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'progress') {
    const fields = fieldsOf(value, ['kind', 'done', 'total']);
    const done = fields?.get('done');
    const total = fields?.get('total');
    return isCount(done) && isCount(total) && done <= total ? { kind, done, total } : null;
  }
  if (kind === 'merged') {
    const chapters = fieldsOf(value, ['kind', 'chapters'])?.get('chapters');
    if (!Array.isArray(chapters) || chapters.length > MAX_BOOK_CHAPTERS) return null;
    const read: MergedChapter[] = [];
    for (const chapter of chapters) {
      const one = readMerged(chapter);
      if (one === null) return null;
      read.push(one);
    }
    return { kind, chapters: read };
  }
  return kind === 'failed' && fieldsOf(value, ['kind']) !== null ? { kind } : null;
}

/** E″1, M″3, M″5: the job a message asks for, or null for anything else (E″5). */
export function readJob(value: unknown): DocxWorkerJob | null {
  const kind: unknown = typeof value === 'object' && value !== null ? Reflect.get(value, 'kind') : undefined;
  if (kind === 'merge-sources') return readSources(value);
  return kind === 'merge' ? readMerge(value) : readDocxJob(value);
}

/** E″1: a DOCX job, or null for anything else. */
function readDocxJob(value: unknown): DocxJob | null {
  const fields = fieldsOf(value, ['kind', 'book', 'chapters', 'preset', 'modified']);
  const modified = fields?.get('modified');
  const chapters = fields?.get('chapters');
  if (fields?.get('kind') !== 'docx' || !isCount(modified) || modified > MAX_MODIFIED || !Array.isArray(chapters)) return null;
  const preset = readPreset(fields.get('preset'));
  if (preset === null) return null;
  const ids: ChapterIds[] = [];
  for (const each of chapters) {
    const read = readIds(each);
    if (read === null) return null;
    ids.push(read);
  }
  let book: PdfBook;
  try {
    book = readExportBook(fields.get('book'));
  } catch {
    return null;
  }
  return book.chapters.length === ids.length ? { kind: 'docx', book, chapters: ids, preset, modified } : null;
}

export { MAX_MODIFIED, readReply } from '../epub/protocol.ts';
